import * as React from "react"
import {
  composeRenderProps,
  TextArea as TextAreaPrimitive,
} from "react-aria-components"

import { cn } from "@/lib/utils"

/**
 * The multiline sibling of `Input` (input.tsx) — same field skin + focus treatment, just taller and auto-growing.
 * Use this for EVERY multiline text field; never a raw `<textarea>` (it inherits none of the token/focus rules and
 * shows the wrong border on focus — see the arc-conventions "Form controls" rule).
 *
 * The focus/border rationale is identical to Input and MUST stay in lockstep with it:
 *  - `border-border` (NOT `border-input`): `--input` == `--card` in dark, so a `border-input` outline vanishes on
 *    drawer/card surfaces. `--input` is reserved for the subtle fill tint (`dark:bg-input/30`).
 *  - FOCUS = one flush brand-`--primary` line (`border-primary` + `ring-1 ring-primary`), no soft `/50` halo. This is
 *    the OWNER-ACCEPTED AA deviation (debate/code-brand-fix.md) — routes focus through the saturated brand for
 *    consistency with the buttons; revert to `ring-ring` for strict AA.
 */
function Textarea({
  className,
  ...props
}: React.ComponentProps<typeof TextAreaPrimitive>) {
  return (
    <TextAreaPrimitive
      data-slot="textarea"
      className={composeRenderProps(className, (className) =>
        cn(
          "field-sizing-content min-h-16 w-full min-w-0 rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
          className
        )
      )}
      {...props}
    />
  )
}

export { Textarea }
